/**
 * How far a work travels, as its owner set it.
 *
 * ORDERED NARROWEST FIRST, and the order is load-bearing rather than
 * cosmetic: `ondo.work_visibility` is declared in this same order so that
 * native enum comparison in Postgres means "at least this wide", and
 * `ondo.region_visible_work_ids()` reads it as `>= 'region'`. Unlike
 * `ACCESS_LEVELS`, which is ordered STRONGEST first because
 * `strongestAccess` relies on it, this array runs the other way — so do not
 * reach for one file's index arithmetic while reading the other.
 *
 * THE FOURTH VALUE ARRIVED, AND IT ARRIVED LAST — OND-172, 20261008092000.
 * `public` was absent rather than present-and-inert while there was nowhere to
 * publish into; `ondo.public_work_view()` is that somewhere. It is appended
 * AFTER `region`, exactly where 20261005098000 decision 4 said it would go, so
 * every comparison written against this order stayed correct when it did.
 *
 * These values must stay in step with the `ondo.work_visibility` enum —
 * declared in
 * supabase/migrations/20261005098000_the_owner_sets_how_far_a_work_travels.sql
 * and widened by 20261008092000_the_fourth_setting_has_a_label.sql. Nothing
 * enforces it but this comment and `supabase/tests/rls.sh`, which pins the
 * enum's labels and their order.
 */
export const WORK_VISIBILITIES = [
    "private",
    "named_parties",
    "region",
    "public",
];
export function isWorkVisibility(value) {
    return (typeof value === "string" && WORK_VISIBILITIES.includes(value));
}
/**
 * The product default, and the one place the client states it.
 *
 * It is a READ default in the database — `ondo.work_estate_disclosure()`
 * answers `named_parties` for a work no owner has set, for a work whose row
 * was written by a former owner, and for every row written before the column
 * existed — so nothing was backfilled and this constant is never written
 * anywhere. It exists so a screen rendering a work whose controls have not
 * loaded yet shows the same rung the server will.
 */
export const DEFAULT_WORK_VISIBILITY = "named_parties";
/**
 * Whether one rung is at least as wide as another, by the declared order.
 *
 * The client's mirror of the `>=` the two SQL doors use, and it is for COPY
 * only: what a reader may actually see is decided by
 * `ondo.region_visible_work_ids()` and `ondo.issue_share_code()`, never here.
 * A screen that gated a control on this rather than on a capability would be
 * deciding authorization in the browser.
 */
export function isAtLeastAsWideAs(level, other) {
    return WORK_VISIBILITIES.indexOf(level) >= WORK_VISIBILITIES.indexOf(other);
}
/**
 * Whether a work at this rung answers a public view — OND-172.
 *
 * A NAMED PREDICATE RATHER THAN `isAtLeastAsWideAs(v, "public")` AT FIVE CALL
 * SITES, because `public` is the top rung today and the comparison reads as
 * though the caller had thought about a wider one. It is COPY ONLY, like the
 * function above: what a public reader may actually see is decided by
 * `ondo.publicly_visible_work_ids()` and `ondo.public_work_view()`, never here.
 */
export function isPublished(level) {
    return isAtLeastAsWideAs(level, "public");
}
//# sourceMappingURL=visibility.js.map